import { useState } from "react";
import { FileSignature, Fingerprint, Plane, ShieldAlert, WalletCards } from "lucide-react";
import { WorkspaceHeader } from "@shared/components/WorkspaceHeader";
import { ChatDisplayContainer } from "@shared/components/ChatDisplayContainer";
import { ChatHub } from "@shared/components/GroupChat/ChatHub";
import { useGlobalNavigationTarget } from "@shared/navigation/globalNavigation";
import { useRrhh } from "../hooks/useRrhh";
import { RrhhSectionNav, type RrhhSection } from "../components/RrhhSectionNav";
import { TimeClockPanel } from "../components/TimeClockPanel";
import { VacationsPanel } from "../components/VacationsPanel";
import { PayrollsPanel } from "../components/PayrollsPanel";
import { ContractsPanel } from "../components/ContractsPanel";
import { RrhhChat } from "../components/RrhhChat";
import { AgendaWorkspace } from "@shared/components/Agenda/AgendaWorkspace";
import "../styles/rrhh.css";

const clockStatusLabels = {
  sin_entrada: "Sin entrada",
  abierto: "En curso",
  cerrado: "Cerrado",
  autocerrado: "Autocerrado",
} as const;

export function RrhhPage() {
  const [active, setActive] = useState<RrhhSection>("fichaje");
  const [chatFloating, setChatFloating] = useState(true);

  useGlobalNavigationTarget("rrhh", (target) => {
    switch (target.section) {
      case "fichaje":
      case "vacaciones":
      case "agenda":
      case "nominas":
      case "documentacion":
        setActive(target.section);
        break;
      default:
        break;
    }
  });

  const { data, loading, reload } = useRrhh();

  return (
    <div className="page-shell !gap-4">
      <WorkspaceHeader
        eyebrow="People Workspace"
        title="Recursos Humanos"
        description="Fichaje, vacaciones, nóminas, contratos y comunicación interna desde una única experiencia de trabajo."
        icon={ShieldAlert}
        loading={loading}
        metrics={[
          { label: "Fichaje", value: clockStatusLabels[data.clock.status], icon: Fingerprint },
          { label: "Vacaciones", value: `${data.vacations.summary.remaining_days} días`, icon: Plane, tone: "emerald" },
          { label: "Nóminas", value: data.payrolls.length, icon: WalletCards },
          { label: "Documentos", value: data.contracts.length, icon: FileSignature },
        ]}
      />

      <div className="dashboard-shell-grid grid min-h-[560px] grid-cols-1 gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
        <div className="dashboard-slide-left dashboard-delay-2">
          <RrhhSectionNav active={active} onChange={setActive} />
        </div>

        <section className="dashboard-component-shell dashboard-slide-right dashboard-delay-2 relative flex min-h-[560px] flex-col overflow-hidden rounded-[34px] transition-all duration-300 hover:-translate-y-1 hover:border-emerald-200 hover:shadow-[0_34px_90px_rgba(15,23,42,.14)]">
          {active === "fichaje" && (
            <TimeClockPanel
              clockData={data.clock}
              loading={loading}
              onReload={reload}
            />
          )}

          {active === "vacaciones" && (
            <VacationsPanel
              vacations={data.vacations.items}
              summary={data.vacations.summary}
              calendarDays={data.vacations.calendar_days}
              constraints={data.vacations.constraints}
              loading={loading}
              onReload={reload}
            />
          )}

          {active === "agenda" && (
            <AgendaWorkspace scope="personal" />
          )}

          {active === "nominas" && (
            <PayrollsPanel payrolls={data.payrolls} loading={loading} onReload={reload} />
          )}

          {active === "documentacion" && (
            <ContractsPanel contracts={data.contracts} loading={loading} onReload={reload} />
          )}
        </section>
      </div>

      <ChatDisplayContainer
        floating={chatFloating}
        label="Chat de Recursos Humanos"
        onChangeFloating={setChatFloating}
      >
        <ChatHub
          scope="rrhh"
          floating={chatFloating}
          direct={<RrhhChat floating={chatFloating} />}
        />
      </ChatDisplayContainer>
    </div>
  );
}
